import { Inject, Injectable, InjectionToken } from '@angular/core';
import type { IConfig, UnleashClient } from 'unleash-proxy-client';
import { environment } from 'src/environments/environment';
import { ServicioAutenticacion } from '../_services/autenticacion.service';
import { FlagContext, FuenteFlag } from './feature-flag.model';

/** Crea el cliente Unleash. Puede ser asíncrona (carga perezosa de la librería). */
export type UnleashClientFactory = (config: IConfig) => UnleashClient | Promise<UnleashClient>;

/**
 * Seam de creación del cliente, para poder usar un cliente falso en los tests.
 *
 * El `import()` es DINÁMICO a propósito: los ~26 kB de `unleash-proxy-client` quedan en
 * un chunk aparte que solo se descarga si Unleash está configurado.
 *
 * Se usa la librería (y no `HttpClient`) por una razón de peso en ESTE repo: la librería
 * llama con `fetch`, así que no pasa por los interceptores. `JwtInterceptor` reemplaza
 * todos los headers de cualquier request con sesión —pisaría la clientKey y mandaría el
 * token del usuario a un tercero— y `ErrorInterceptor` ante un 401 intenta refrescar y
 * hace logout si falla. Un flag no puede sacar a nadie de la aplicación.
 */
export const UNLEASH_CLIENT_FACTORY = new InjectionToken<UnleashClientFactory>(
  'UNLEASH_CLIENT_FACTORY',
  {
    providedIn: 'root',
    factory: () => async (config: IConfig) => {
      const { UnleashClient } = await import('unleash-proxy-client');
      return new UnleashClient(config);
    }
  }
);

/**
 * Fachada de feature flags.
 *
 * Regla, y es una sola: **si Unleash contestó, Unleash manda. Si no contestó, manda
 * `environment.flags`.**
 *
 * La Frontend API devuelve solo los toggles ENCENDIDOS para el contexto, así que "no vino"
 * cubre por igual al flag apagado y al inexistente; los dos se tratan como apagado. Eso
 * preserva el kill-switch: si al no encontrarlo se cayera al valor local, un flag con
 * `environment.flags` en `true` quedaría imposible de apagar desde la consola.
 *
 * Corolario: `environment.flags` es SOLO la red de emergencia para cuando Unleash no
 * responde. Mantenerlo en `false` salvo que se quiera forzar el comportamiento.
 */
@Injectable({ providedIn: 'root' })
export class FeatureFlagService {
  private client: UnleashClient | null = null;
  private ready = false;
  private listoPromesa: Promise<void> | null = null;

  constructor(
    private auth: ServicioAutenticacion,
    @Inject(UNLEASH_CLIENT_FACTORY) private crearCliente: UnleashClientFactory
  ) { }

  /** true si hay config de Unleash. Con `url` o `clientKey` vacíos no se llama a nadie. */
  get isActive(): boolean {
    return !!environment.unleash && !!environment.unleash.url && !!environment.unleash.clientKey;
  }

  /**
   * Arranca el cliente. Se llama desde un APP_INITIALIZER SIN esperarlo: la app no se
   * demora por Unleash. Quien necesite el valor remoto antes de decidir (un guard, por
   * ejemplo) espera `listo()`.
   *
   * Nunca lanza: un fallo de Unleash no puede impedir que la app arranque.
   */
  init(): void {
    this.listo();
  }

  /**
   * Resuelve cuando los flags ya se pueden consultar: con la primera respuesta de
   * Unleash, con un error del cliente, o al vencer `timeoutMs`. Nunca rechaza.
   *
   * El tope es 2500 ms y no menos: la llamada lleva header `Authorization`, así que el
   * navegador hace un preflight CORS antes del GET. Medido en Login desde Chile, son
   * ~850 ms la primera vez; un tope más corto cortaba la evaluación casi siempre.
   */
  listo(): Promise<void> {
    if (this.listoPromesa) {
      return this.listoPromesa;
    }
    if (!this.isActive) {
      this.listoPromesa = Promise.resolve();
      return this.listoPromesa;
    }

    this.listoPromesa = new Promise<void>(resolve => {
      let resuelto = false;
      const terminar = () => {
        if (resuelto) { return; }
        resuelto = true;
        clearTimeout(temporizador);
        resolve();
      };
      const temporizador = setTimeout(terminar, environment.unleash.timeoutMs);

      Promise.resolve()
        .then(() => this.crearCliente({
          url: environment.unleash.url,
          clientKey: environment.unleash.clientKey,
          appName: environment.unleash.appName,
          refreshInterval: environment.unleash.refreshInterval,
          context: this.contexto()
        }))
        .then(cliente => {
          this.client = cliente;
          cliente.on('ready', () => { this.ready = true; terminar(); });
          // Errores de red, token inválido, CORS, adblocker: se silencian y manda el
          // fallback local. El cliente sigue reintentando en cada `refreshInterval`.
          cliente.on('error', () => terminar());
          return cliente.start();
        })
        .catch(() => terminar());
    });
    return this.listoPromesa;
  }

  /** Evalúa un flag. Síncrono: usa lo último que respondió Unleash o el fallback local. */
  isEnabled(flag: string): boolean {
    if (this.fuente() === 'unleash' && this.client) {
      return this.client.isEnabled(flag);
    }
    return this.flagLocal(flag);
  }

  /** De dónde saldría hoy el valor de un flag. */
  fuente(): FuenteFlag {
    return this.ready && this.client ? 'unleash' : 'environment';
  }

  /**
   * Contexto de evaluación desde el token de permisos.
   *
   * No lanza nunca: `decodeToken` tira con un `tokenPermisos` corrupto, y un contexto
   * vacío simplemente no calza con los constraints — o sea, flag apagado.
   */
  contexto(): { userId?: string; properties: Record<string, string> } {
    let ctx: FlagContext = {};
    try {
      const p = this.auth.getCurrentUserPermissionValue();
      ctx = {
        userId: p.IdUsuario || undefined,
        IdEmpresa: p.IdEmpresa || undefined,
        IdOrganizacion: p.IdOrganizacion || undefined,
        Pais: p.Pais || undefined
      };
    } catch {
      ctx = {};
    }

    const properties: Record<string, string> = {};
    if (ctx.IdEmpresa) { properties['IdEmpresa'] = String(ctx.IdEmpresa); }
    if (ctx.IdOrganizacion) { properties['IdOrganizacion'] = String(ctx.IdOrganizacion); }
    if (ctx.Pais) { properties['Pais'] = ctx.Pais; }
    return { userId: ctx.userId, properties };
  }

  /** Valor del flag en `environment.flags`. Ausente => apagado. */
  private flagLocal(flag: string): boolean {
    const flags: Record<string, boolean> = environment.flags || {};
    return flags[flag] === true;
  }
}
